import React, { useCallback, useEffect, useState } from 'react';
import Dashboard from 'domain/entity/dashboard';
import { useReadLinks } from 'adapter/in/dashboards/hooks/Links/useReadLinks';
import { CopiedFrom, copyDashboardLink } from 'adapter/in/dashboards/utils/copyDashboardLink';
import { getHierarchyIcon } from 'adapter/in/dashboards/utils/getHierarchyIcon';
import useTimeout from 'adapter/in/dashboards/utils/useTimeout';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import DashboardLinkModal from 'adapter/in/dashboards/pages/Modals/LinkModal';

export const useOnLinkDashboardModal = () => {
    const [showLinkModal, setShowLinkModal] = useState(false);
    const {
        link,
        isLoading: isLoadingLinkModal,
        hasError: hasErrorLinkModal,
        reset: resetReadLinks,
        handleReadLinks,
        handleReadLinksAgain,
        handleEnableLink,
        handleDeleteLink,
        handleLocations,
    } = useReadLinks();
    const [activeModalDashboard, setActiveModalDashboard] = useState<Dashboard>();
    const {
        profile: { locationName, locationType, selectedLocationTags },
    } = useProfile();
    const [linkedUrl, setLinkedUrl] = useState<string>('');
    const [isChecked, setIsChecked] = useState<boolean>();
    const [showResetContainer, setShowResetContainer] = useState(false);
    const [showLinkTooltip, setShowLinkTooltip] = useState(false);
    const [showEmbedTooltip, setShowEmbedTooltip] = useState(false);
    const [showResetTooltip, setShowResetTooltip] = useState(false);
    const { cancelTimeout: cancelTimeoutEmbed } = useTimeout(() => {
        setShowEmbedTooltip(false);
    }, 3000);
    const { cancelTimeout: cancelTimeoutLink } = useTimeout(() => {
        setShowLinkTooltip(false);
    }, 3000);
    const { cancelTimeout: cancelTimeoutReset } = useTimeout(() => {
        setShowResetTooltip(false);
    }, 3000);

    const disabledButtons = isLoadingLinkModal || linkedUrl === '';

    const tags = [
        {
            label: locationName,
            icon: getHierarchyIcon(locationType),
        },
        // Not yet available
        // {
        //     label: "Last 7 days",
        //     icon: "calendar_range"
        // },
        ...selectedLocationTags.map((tag) => ({
            label: tag,
            icon: 'tag',
        })),
    ];

    useEffect(() => {
        setLinkedUrl(link?.linkedUrl ?? '');
        setIsChecked(link?.active ?? true);
    }, [link]);

    const handleOnLink = useCallback((dashboard: Dashboard) => {
        setShowLinkModal(true);
        handleReadLinks(dashboard.id, dashboard.name);
        setActiveModalDashboard(dashboard);
    }, []);

    const handleEnableLinkModal = (id: string, active: boolean) => handleEnableLink(id, active);
    const handleResetLinkModal = (id: string) => {
        handleDeleteLink(id);
    };
    const handleCSVLinksModal = (id: string) => handleLocations(id);

    const handleOnCopyLinkModal = useCallback(
        (from: CopiedFrom) => (activeModalDashboard ? copyDashboardLink(from, activeModalDashboard.name, link?.linkedUrl) : null),
        [link?.linkedUrl, activeModalDashboard]
    );

    const handleCloseModal = () => {
        setShowLinkTooltip(false);
        setShowEmbedTooltip(false);
        setShowResetTooltip(false);
        setShowResetContainer(false);
        setShowLinkModal(false);
        resetReadLinks();
    };

    const handleCheckSwitch = useCallback(
        (value: boolean) => {
            if (link) {
                setIsChecked(value);
                handleEnableLinkModal(link?.id, value);
            }
        },
        [link]
    );

    const handleCopy = useCallback(
        (from: CopiedFrom) => {
            if (linkedUrl !== '') {
                handleOnCopyLinkModal(from);
            }
        },
        [linkedUrl, handleOnCopyLinkModal]
    );

    const handleResetLink = useCallback(() => {
        if (link) {
            handleResetLinkModal(link.id);
            setShowResetTooltip(true);
            handleShowResetActionModal(!showResetContainer);
            cancelTimeoutReset();
        }
    }, [link]);

    const handleCSVDownload = useCallback(() => {
        if (link) {
            handleCSVLinksModal(link.id);
        }
    }, [link]);

    const handleShowResetActionModal = useCallback((value: boolean) => {
        setShowResetContainer(value);
    }, []);

    const handleCopyLink = () => {
        handleCopy(CopiedFrom.LINK);
        setShowLinkTooltip(true);
        cancelTimeoutLink();
    };

    const handleCopyHTML = () => {
        handleCopy(CopiedFrom.HTML);
        setShowEmbedTooltip(true);
        cancelTimeoutEmbed();
    };

    const onLinkModal = (
        <DashboardLinkModal
            disabledButtons={disabledButtons}
            error={hasErrorLinkModal}
            isLoading={isLoadingLinkModal}
            isChecked={isChecked}
            link={link}
            showModal={showLinkModal}
            showResetContainer={showResetContainer}
            showResetTooltip={showResetTooltip}
            showLinkTooltip={showLinkTooltip}
            showEmbedTooltip={showEmbedTooltip}
            tags={tags}
            handleCloseModal={handleCloseModal}
            handleCopyLink={handleCopyLink}
            handleCopyHTML={handleCopyHTML}
            handleCheckSwitch={handleCheckSwitch}
            handleResetLink={handleResetLink}
            handleCSVLinksDownload={handleCSVDownload}
            handleShowResetActionModal={handleShowResetActionModal}
            onTryAgain={handleReadLinksAgain}
        />
    );

    return { handleOnLink, onLinkModal };
};
